import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@/shared/ui/Icon'
import { composerDisclaimer } from '../mock'
import { useConversations } from '@/features/conversations/ConversationsProvider'
import { ActiveScopeBadge, ScopeSelector, useScope } from '@/features/scope'
import { ReferencePicker, useReferenceSelection } from '@/features/references'
import { AttachFileTrigger } from '@/features/ingestion'
import { useAppState } from '@/app/providers/AppStateProvider'

const TEXTAREA_MAX_PX = 160

/**
 * POLISH 1C — GPT-like compact pill composer.
 *
 * Single-row layout: the attach trigger is now the "+" button on the far
 * left (ChatGPT pattern — it replaces the old paperclip, same behaviour),
 * followed by the compact mic/scope/references icon cluster, the one-line
 * auto-growing textarea, and the round send button on the right. Labels
 * surface via native tooltips + aria-label. The active scope chip renders
 * in a slim row above the input only when a non-default scope is selected,
 * so the resting bar stays a single compact line. All behaviour is
 * unchanged: Ctrl/⌘+Enter to send, attach/scope/reference popovers, the
 * disabled/sending states, and every data-testid. The prompt-first Home
 * hero (HomePromptScreen) is a separate surface and remains untouched.
 */
export function Composer() {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const sendBtnRef = useRef<HTMLButtonElement>(null)
  const scopeBtnRef = useRef<HTMLButtonElement>(null)
  const refsBtnRef = useRef<HTMLButtonElement>(null)
  const [value, setValue] = useState('')
  const { sendMessage, sending } = useConversations()
  const { selectedIds: selectedReferenceIds } = useReferenceSelection()
  const { selectedCaseDocuments } = useScope()
  // Step 13.7-FIX — overlay open-state lives in AppStateProvider so a
  // no_context message's recovery actions can open these same overlays.
  const {
    scopeSelectorOpen,
    openScopeSelector,
    closeScopeSelector,
    toggleScopeSelector,
    referencePickerOpen,
    closeReferencePicker,
    toggleReferencePicker,
  } = useAppState()

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_PX)}px`
  }, [value])

  const inFlightRef = useRef(false)
  const submit = useCallback(async () => {
    // Double-tap guard: even if React renders before `sending` flips true,
    // the ref blocks a back-to-back submit.
    if (inFlightRef.current) return
    const text = value.trim()
    if (!text || sending) return
    inFlightRef.current = true
    setValue('')
    sendBtnRef.current?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(.92)' }, { transform: 'scale(1)' }],
      { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' },
    )
    try {
      await sendMessage(text)
    } finally {
      inFlightRef.current = false
    }
  }, [value, sending, sendMessage])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        void submit()
      }
    },
    [submit],
  )

  const hasText = value.trim().length > 0
  // ActiveScopeBadge (compact) renders null for the default "Todo o
  // workspace" scope — gate the chips row on the same condition so we never
  // reserve an empty strip above the input.
  const hasScopeChip = selectedCaseDocuments.length > 0

  return (
    <div className="composer-wrap">
      <div className={`composer relative${sending ? ' is-sending' : ''}`}>
        {hasScopeChip && (
          <div className="composer-chips">
            <ActiveScopeBadge compact onOpen={openScopeSelector} />
          </div>
        )}

        <div className="composer-row">
          <AttachFileTrigger
            className="composer-tool"
            title="Anexar arquivo ao chat"
            ariaLabel="Anexar arquivo"
            disabled={sending}
          >
            <Icon name="plus" />
          </AttachFileTrigger>
          <button
            type="button"
            className="composer-tool"
            title="Ditar (em breve)"
            aria-label="Ditar (em breve)"
          >
            <Icon name="mic" />
          </button>
          <button
            ref={scopeBtnRef}
            type="button"
            className="composer-tool"
            data-testid="scope-trigger"
            title="Definir escopo do RAG"
            aria-label="Definir escopo do RAG"
            aria-haspopup="dialog"
            aria-expanded={scopeSelectorOpen}
            onClick={toggleScopeSelector}
          >
            <Icon name="grid" />
          </button>
          <button
            ref={refsBtnRef}
            type="button"
            className="composer-tool"
            data-testid="references-trigger"
            title="Selecionar referências para esta consulta"
            aria-label="Selecionar referências para esta consulta"
            aria-haspopup="dialog"
            aria-expanded={referencePickerOpen}
            onClick={toggleReferencePicker}
          >
            <Icon name="book" />
            {selectedReferenceIds.length > 0 && (
              <span
                className="composer-tool-count"
                aria-label={`${selectedReferenceIds.length} referências selecionadas`}
              >
                {selectedReferenceIds.length}
              </span>
            )}
          </button>

          <textarea
            ref={textareaRef}
            rows={1}
            data-testid="composer-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              sending ? 'Aguardando resposta…' : 'Pergunte sobre seus documentos…'
            }
            aria-label="Mensagem"
            aria-busy={sending}
            disabled={sending}
          />

          <button
            ref={sendBtnRef}
            type="button"
            className="send-btn"
            data-testid="composer-send"
            title="Enviar (Ctrl+Enter)"
            aria-label="Enviar mensagem"
            disabled={!hasText || sending}
            onClick={() => void submit()}
          >
            <Icon name="arrow-up" />
          </button>
        </div>

        <ScopeSelector
          open={scopeSelectorOpen}
          onClose={closeScopeSelector}
          anchorRef={scopeBtnRef}
        />
        <ReferencePicker
          open={referencePickerOpen}
          onClose={closeReferencePicker}
          anchorRef={refsBtnRef}
        />
      </div>

      <div className="composer-note">{composerDisclaimer}</div>
    </div>
  )
}
